import OverlayWindow from 'Components/OverlayWindow';
import Spinner from 'Components/Spinner';
import { useCurrentPopup } from 'Components/Popups';
import { useLazyGetClientRequirementsQuery } from 'app/services/clientRequirements';
import { useLazyGetOrdersQuery } from 'app/services/payments/orders';
import {
  useLazyGetPaginatedTasksQuery,
  useGetTaskByIdQuery,
  ITask,
} from 'app/services/tasks';
import { useAuth } from 'utils/AuthProvider';
import { useEffect, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router-dom';
import { SignModal } from 'Components/SignModal';

export default function HomeworkModal() {
  const { close, lock, unlock } = useCurrentPopup();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [signTaskId, setSignTaskId] = useState<number | null>(null);

  const { data: signTask } = useGetTaskByIdQuery(
    signTaskId !== null ? signTaskId.toString() : skipToken,
    {
      skip: signTaskId === null,
    },
  );

  // When a sign task is active, prevent closing the homework modal
  useEffect(() => {
    if (signTaskId !== null) {
      lock();
    } else {
      unlock();
    }
  }, [signTaskId, lock, unlock]);

  const [fetchRequirements, { data: requirements, isFetching: loadingReqs }] =
    useLazyGetClientRequirementsQuery();
  const [fetchOrders, { data: ordersData, isFetching: loadingOrders }] =
    useLazyGetOrdersQuery();
  const [fetchTasks, { data: tasksData, isFetching: loadingTasks }] =
    useLazyGetPaginatedTasksQuery();

  useEffect(() => {
    if (user) {
      fetchRequirements({ client_id: user.id });
      fetchOrders({ filters: { state: 'pending_payment' }, per_page: 20 });
      fetchTasks({ filters: { client_id: user.id }, per_page: 20 });
    }
  }, [user, fetchRequirements, fetchOrders, fetchTasks]);

  const pendingRequirements = requirements?.filter(
    (r) => r.status === 'pending',
  );
  const pendingOrders = ordersData?.data ?? [];
  const unsignedLetters = tasksData?.data?.filter(
    (t) => t.engagement_letter?.signing_status === 'pending',
  );

  const isLoading = loadingReqs || loadingOrders || loadingTasks;

  const go = (path: string) => {
    close();
    navigate(path);
  };

  return (
    <>
      <OverlayWindow title="Pending Assignments" onClose={close}>
        {isLoading ? (
          <Spinner />
        ) : (
          <div className="space-y-4 text-sm text-gray-700">
            {pendingRequirements && pendingRequirements.length > 0 && (
              <div>
                <h3 className="font-semibold mb-1">Requirements</h3>
                <ul className="list-disc ml-5 space-y-1">
                  {pendingRequirements.map((req) => (
                    <li key={req.id}>
                      <button
                        className="underline text-green"
                        onClick={() =>
                          go(`/dashboard/tasks/${req.task_id}/requirements`)
                        }
                      >
                        {req.description}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {unsignedLetters && unsignedLetters.length > 0 && (
              <div>
                <h3 className="font-semibold mb-1">Engagement Letters</h3>
                <ul className="list-disc ml-5 space-y-1">
                  {unsignedLetters.map((task) => (
                    <li key={task.id}>
                      <button
                        className="underline text-green"
                        onClick={() => setSignTaskId(task.id)}
                      >
                        {task.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pendingOrders && pendingOrders.length > 0 && (
              <div>
                <h3 className="font-semibold mb-1">Payments</h3>
                <ul className="list-disc ml-5 space-y-1">
                  {pendingOrders.map((order) => {
                    const title =
                      order.order_items?.[0]?.product_meta?.title ||
                      `Order #${order.id}`;
                    return (
                      <li key={order.id}>
                        <button
                          className="underline text-green"
                          onClick={() => go('/dashboard/orders')}
                        >
                          {title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {(!pendingRequirements || pendingRequirements.length === 0) &&
              (!unsignedLetters || unsignedLetters.length === 0) &&
              (!pendingOrders || pendingOrders.length === 0) && (
                <div>No pending items!</div>
              )}
          </div>
        )}
      </OverlayWindow>
      {signTask && signTask.engagement_letter && (
        <SignModal
          previewUrl={signTask.engagement_letter.preview_url}
          signUrl={signTask.engagement_letter.sign_url}
          engagementId={signTask.engagement_letter.id}
          onClose={() => setSignTaskId(null)}
          onSigned={() => setSignTaskId(null)}
        />
      )}
    </>
  );
}
